import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Descriptions, Space, Tooltip, Typography, Upload } from 'antd'
import { FileExcelOutlined, FileGifOutlined, FileImageOutlined, FileJpgOutlined, FilePdfOutlined, FilePptOutlined, FileTextOutlined, FileUnknownOutlined, FileWordOutlined, FileZipOutlined, PlusOutlined } from '@ant-design/icons'
import { moment } from '@BUP/configs/i18n'
import { UtilsProvider } from '@BUP/contexts/utils-provider.jsx'
import { getApi } from '@BUP/services/Axios'
import { getToken } from '@BUP/services/auth'

const FileWall = ({ attachments, mutate, action, headers }) => {
  const i18n = useTranslation()
  const [fileList, setFileList] = useState()
  useEffect(() => {
    setFileList(attachments)
  }, [attachments])

  const handleDownload = async file => {
    const client = await getApi('schools', { authenticated: true, version: 4 })
    const { link: href } = await client.get(`/complaint-entry-attachments/${file.uid}/download`)
      .then(response => response.data)
    const link = document.createElement('a')
    link.download = file.name
    link.href = href
    link.target = '_blank'
    link.click()
    link.remove()
  }
  const handleChange = ({ file, fileList }) => {
    setFileList(fileList)
    if (file.status === 'done') {
      mutate()
    }
  }

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: 'none'
      }}
      type='button'
    >
      <PlusOutlined />
      <div
        style={{
          marginTop: 8
        }}
      >
        {i18n.t('common.resources.complaint-entry-attachments.actions.upload.submit')}
      </div>
    </button>
  )
  return (
    <Upload
      action={action}
      headers={headers}
      listType='picture-card'
      fileList={fileList}
      onDownload={handleDownload}
      onChange={handleChange}
      isImageUrl={() => false}
      showUploadList={{
        showPreviewIcon: false,
        showRemoveIcon: false,
        showDownloadIcon: true
      }}
      itemRender={(originNode, file) => {
        const title = file.createdAt && i18n.t('common.resources.complaint-entry-attachments.attributes.created-by.label_with-username-and-date', {
          username: file.creator.user.name,
          date: moment(file.createdAt).format('LLLL')
        })
        return <Tooltip placement='bottom' title={title}>{originNode}</Tooltip>
      }}
      iconRender={file => {
        switch (file.mimetype) {
          case 'text/plain':
            return <FileTextOutlined />
          case 'application/pdf':
            return <FilePdfOutlined />
          case 'image/gif':
            return <FileGifOutlined />
          case 'image/jpeg':
          case 'image/jpg':
            return <FileJpgOutlined />
          case 'image/png':
            return <FileImageOutlined />
          case 'application/msword':
          case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return <FileWordOutlined />
          case 'application/vnd.ms-excel':
          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return <FileExcelOutlined />
          case 'application/vnd.ms-powerpoint':
          case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return <FilePptOutlined />
          case 'application/x-bzip':
          case 'application/x-bzip2':
          case 'application/gzip':
          case 'application/x-tar':
          case 'application/zip':
            return <FileZipOutlined />
          default:
            return <FileUnknownOutlined />
        }
      }}
    >
      {uploadButton}
    </Upload>
  )
}

export function ComplaintEntryDescriptions ({ complaintEntry, mutate }) {
  const i18n = useTranslation('translation', { keyPrefix: 'common.resources.complaint-entry' })
  const [token, setToken] = useState()
  const [action, setAction] = useState()
  useEffect(() => {
    getToken()
      .then(setToken)
    getApi('schools', { version: 4 })
      .then(client => client.getUri({ url: `/current-enrollment/complaint-entries/${complaintEntry.id}/complaint-entry-attachments` }))
      .then(setAction)
  }, [complaintEntry.id])

  const attachments = useMemo(() => complaintEntry.complaintEntryAttachments.map(attachment => ({
    name: attachment.metadata.filename,
    uid: attachment.id,
    url: attachment.id,
    mimetype: attachment.metadata.mimetype,
    status: 'done',
    isImageUrl: false,
    creator: attachment.creator,
    createdAt: attachment.createdAt
  })), [complaintEntry.complaintEntryAttachments])

  if (!token || !action || !attachments) return null

  const headers = {
    Authorization: `Bearer ${token}`
  }

  return (
    <Space direction='vertical' size='middle'>
      <Descriptions column={2} layout='vertical' size='small' labelStyle={{ fontWeight: 'bold' }} bordered>
        <Descriptions.Item span={2} label={i18n.t('attributes.type.label')}>
          <Typography.Text>{i18n.t(`values.type.${complaintEntry.type}.label`)}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item span={2} label={i18n.t('attributes.title.label')}>
          <Typography.Text>{complaintEntry.title}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item span={2} label={i18n.t('attributes.content.label')}>
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{complaintEntry.content}</Typography.Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t('attributes.created-at.label')}>
          <Typography.Text>
            <time dateTime={complaintEntry.createdAt}>{moment(complaintEntry.createdAt).format('LLLL')}</time> ({(moment(complaintEntry.createdAt).fromNow())})
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t('attributes.created-by.label')}>
          <Typography.Text>
            <address>{complaintEntry.creator.user.name}</address>
          </Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      <UtilsProvider>
        <Typography.Title level={4}>{i18n.t('attributes.attachments.label')}</Typography.Title>
        <FileWall attachments={attachments} mutate={mutate} action={action} headers={headers} />
      </UtilsProvider>
    </Space>
  )
}
